import React, { useEffect, useState, useCallback, useRef } from "react";

import CreatableSelect from "react-select/creatable";
import Select from "react-select";

import { FieldProps, getIn } from "formik";
import { FormGroup } from "react-bootstrap";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";

interface Option {
  readonly label: string;
  readonly value: string;
}

type Props = {
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: Option | null;
  createFn?: (newValue: string) => Promise<{ name: string; id?: string; value?: string }>;
  defaultOptions: Option[];
  onChange: (value: any, label?: string) => any;
  isInvalid?: boolean;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
};

export const QboSelect: React.FC<Props> = ({
  createFn,
  defaultOptions,
  onChange,
  type,
  value,
  isInvalid,
  loadOptions,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [selectedValue, setSelectedValue] = useState<Option | null>(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    setOptions(defaultOptions);
  }, [defaultOptions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  const handleCreate = async (inputValue: string) => {
    if (!createFn) return;
    
    setIsLoading(true);
    try {
      const rsp = await createFn(inputValue);
      const newOption = { label: rsp.name, value: rsp.value || rsp.id };
      setOptions((prev) => [newOption, ...prev]);
      setSelectedValue(newOption);
      // Pass both value and label so parent can use the name immediately
      onChange(newOption.value, newOption.label);
    } catch (err) {
      console.error("Error creating item:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const commonStyles = {
    control: (base, state) => ({
      ...base,
      border: isInvalid ? "1px solid #e55353 !important" : "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": {
        border: isInvalid
          ? "1px solid #e55353 !important"
          : "1px solid #ced4da",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // When we have loadOptions: drive options ourselves via onInputChange so the backend is always called when the user types
  const [asyncOptions, setAsyncOptions] = useState<Option[]>(defaultOptions);
  useEffect(() => {
    if (!loadOptions) return;
    setAsyncOptions(defaultOptions || []);
  }, [defaultOptions, loadOptions]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      if (!loadOptions) return;
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
        loadOptionsTimeoutRef.current = null;
      }
      const term = (inputValue || "").trim();
      if (!term) {
        setAsyncOptions(defaultOptions ? defaultOptions.slice(0, 50) : []);
        return;
      }
      loadOptionsTimeoutRef.current = setTimeout(() => {
        loadOptionsTimeoutRef.current = null;
        loadOptions(term)
          .then((opts) => setAsyncOptions(opts || []))
          .catch(() => setAsyncOptions([]));
      }, 300);
    },
    [loadOptions, defaultOptions]
  );

  // Get the type label for create option
  const getTypeLabel = () => {
    switch (type) {
      case "customer":
        return "customer";
      case "vendor":
        return "vendor";
      case "account":
        return "account";
      case "category":
        return "category";
      case "service":
        return "service/item";
      case "tax":
        return "tax code";
      default:
        return "item";
    }
  };

  // loadOptions + createFn: use CreatableSelect and call backend from onInputChange so the request is always sent when typing
  if (loadOptions && createFn) {
    return (
      <CreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        isDisabled={isLoading}
        isLoading={isLoading}
        options={asyncOptions}
        filterOption={() => true}
        onInputChange={handleInputChange}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        onCreateOption={handleCreate}
        value={selectedValue}
        styles={commonStyles}
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : `No ${getTypeLabel()} found - type to create`
        }
      />
    );
  }

  // loadOptions only (no create): use Select and call backend from onInputChange
  if (loadOptions) {
    return (
      <Select
        placeholder="Type to search"
        {...rest}
        isClearable
        options={asyncOptions}
        filterOption={() => true}
        onInputChange={handleInputChange}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        value={selectedValue}
        styles={commonStyles}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No options found"
        }
      />
    );
  }

  // Use CreatableSelect if createFn is provided (no async loading)
  if (createFn) {
    return (
      <CreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        isDisabled={isLoading}
        isLoading={isLoading}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        onCreateOption={handleCreate}
        options={options}
        value={selectedValue}
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        styles={commonStyles}
      />
    );
  }

  // Default: regular Select (no create, no async)
  return (
    <Select
      placeholder="Type to search"
      {...rest}
      isClearable
      onChange={(newValue) => {
        setSelectedValue(newValue ? newValue : null);
        onChange(newValue ? newValue.value : null);
      }}
      options={options}
      value={selectedValue}
      styles={commonStyles}
    />
  );
};

export const FormikQboSelect = ({
  createFn,
  onChangeFn,
  type,
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  value,
  loadOptions,
  ...props
}: FieldProps & {
  label: string;
  options: Array<{ value: string; label: string }>;
  createFn?: any;
  onChangeFn?: any;
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: any;
  style?: React.CSSProperties;
  dataCy?: string;
  loadOptions?: (
    inputValue: string
  ) => Promise<Array<{ value: string; label: string }>>;
}) => {
  const { options } = props;
  const isInvalid = getIn(touched, field.name) && getIn(errors, field.name);
  const error = getIn(errors, field.name);

  return (
    <FormGroup
      style={props.style ? props.style : {}}
      data-cy={props.dataCy ? props.dataCy : ""}
    >
      {props.label && <label htmlFor={field.name}>{props.label}</label>}
      <QboSelect
        {...field}
        {...props}
        defaultOptions={options}
        value={value}
        onChange={(option, label) => {
          setFieldValue(field.name, option ? option : "");
          if (onChangeFn) {
            // Pass label as second argument when available (for newly created items)
            onChangeFn(option, label);
          }
        }}
        createFn={createFn}
        type={type}
        isInvalid={!!isInvalid}
        loadOptions={loadOptions}
      />
      {isInvalid && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};
